
import { useLanguage } from '@/contexts/LanguageContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { motion } from 'framer-motion';
import { TableOfContents } from '@/components/TableOfContents';
import { Link } from 'react-router-dom';
import { ChevronLeft, ExternalLink } from 'lucide-react';

const FindRightSchool = () => {
  const { language } = useLanguage();

  // Content for the article
  const sections = [
    {
      id: 'introduction',
      title: language === 'de' ? 'Einleitung' : 'Introduction',
      content: language === 'de' 
        ? 'Die richtige Hundeschule für deinen Vierbeiner zu finden, ist eine wichtige Entscheidung. Mit der richtigen Ausbildung kann dein Hund zu einem glücklichen und gehorsamen Begleiter werden.'
        : 'Finding the right dog school for your four-legged friend is an important decision. With proper training, your dog can become a happy and obedient companion.'
    },
    {
      id: 'criteria',
      title: language === 'de' ? 'Auswahlkriterien' : 'Selection Criteria',
      content: language === 'de'
        ? 'Bei der Auswahl einer Hundeschule solltest du auf Qualifikationen der Trainer, Trainingsmethoden, Gruppengröße, Standort und Kosten achten. Besuche die Schule vorab und sprich mit anderen Hundebesitzern über ihre Erfahrungen.'
        : 'When choosing a dog school, pay attention to trainer qualifications, training methods, group size, location, and costs. Visit the school beforehand and talk to other dog owners about their experiences.'
    },
    {
      id: 'training-methods',
      title: language === 'de' ? 'Trainingsmethoden' : 'Training Methods',
      content: language === 'de'
        ? 'Es gibt verschiedene Trainingsansätze, von positiver Verstärkung bis hin zu korrigierenden Methoden. Wähle eine Methode, die zu deinem Hund passt und ethisch vertretbar ist. Moderne Hundeschulen setzen zunehmend auf belohnungsbasiertes Training.'
        : 'There are various training approaches, from positive reinforcement to corrective methods. Choose a method that suits your dog and is ethically sound. Modern dog schools increasingly rely on reward-based training.'
    },
    {
      id: 'first-lesson',
      title: language === 'de' ? 'Die erste Unterrichtsstunde' : 'The First Lesson',
      content: language === 'de'
        ? 'Achte bei der ersten Stunde darauf, wie der Trainer mit den Hunden interagiert. Ein guter Trainer sollte geduldig sein und individuell auf jeden Hund eingehen können. Die Atmosphäre sollte entspannt und positiv sein.'
        : 'During the first lesson, pay attention to how the trainer interacts with the dogs. A good trainer should be patient and able to cater to each dog individually. The atmosphere should be relaxed and positive.'
    },
    {
      id: 'specialized-training',
      title: language === 'de' ? 'Spezialisierte Ausbildung' : 'Specialized Training',
      content: language === 'de'
        ? 'Manche Hundeschulen bieten spezialisierte Kurse für bestimmte Rassen oder spezifische Probleme wie Aggression oder Angst an. Wenn dein Hund besondere Bedürfnisse hat, suche nach einer Schule mit entsprechender Erfahrung.'
        : 'Some dog schools offer specialized courses for certain breeds or specific problems such as aggression or anxiety. If your dog has special needs, look for a school with relevant experience.'
    },
    {
      id: 'success-signs',
      title: language === 'de' ? 'Anzeichen für erfolgreiche Ausbildung' : 'Signs of Successful Training',
      content: language === 'de'
        ? 'Eine erfolgreiche Ausbildung zeigt sich nicht nur im Gehorsam des Hundes, sondern auch in seiner allgemeinen Zufriedenheit. Dein Hund sollte nach dem Training entspannt und glücklich wirken, nicht gestresst oder verängstigt.'
        : 'Successful training is evident not only in the dog\'s obedience but also in its overall satisfaction. Your dog should appear relaxed and happy after training, not stressed or frightened.'
    },
    {
      id: 'conclusion',
      title: language === 'de' ? 'Fazit' : 'Conclusion',
      content: language === 'de'
        ? 'Die richtige Hundeschule kann eine wunderbare Investition in die Beziehung zu deinem Hund sein. Nimm dir Zeit bei der Auswahl und vertraue deinem Instinkt. Eine gute Schule sollte sowohl für dich als auch für deinen Hund eine positive Erfahrung sein.'
        : 'The right dog school can be a wonderful investment in your relationship with your dog. Take your time when choosing and trust your instincts. A good school should be a positive experience for both you and your dog.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row gap-8"
        >
          <Link to="/" className="inline-flex items-center text-primary hover:underline mb-6">
            <ChevronLeft size={20} />
            <span>{language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}</span>
          </Link>

          <div className="lg:w-3/4">
            <h1 className="text-3xl font-bold mb-6 text-gray-900">
              {language === 'de' ? 'Die richtige Hundeschule finden' : 'Finding the Right Dog School'}
            </h1>
            
            {sections.map((section) => (
              <section key={section.id} id={section.id} className="mb-8">
                <h2 className="text-xl font-semibold mb-3 text-gray-800">{section.title}</h2>
                <p className="text-gray-600 leading-relaxed">{section.content}</p>
              </section>
            ))}
            
            {/* External Resource Link */}
            <div className="mt-12 mb-8 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">
                {language === 'de' ? 'Weiterführende Informationen' : 'Additional Resources'}
              </h3>
              <a 
                href="https://www.peta.de/themen/hundeschule/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                {language === 'de' 
                  ? 'PETA: Tipps zur Hundeerziehung und Hundeschulen' 
                  : 'PETA: Tips on Dog Training and Dog Schools'}
                <ExternalLink size={16} />
              </a>
            </div>
          </div>
          
          <div className="lg:w-1/4 mb-6 lg:mb-0 lg:sticky lg:top-24 lg:self-start">
            <TableOfContents 
              items={sections.map(s => ({ id: s.id, title: s.title }))} 
              title={language === 'de' ? 'Inhaltsverzeichnis' : 'Table of Contents'}
            />
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default FindRightSchool;
